import { useTranslation } from 'react-i18next'
import type { ChapterReportView } from '@/types/chapterReport'
import { ChapterLearningSection } from './ChapterLearningSection'
import { ChapterReportHeader } from './ChapterReportHeader'
import { ChapterTestSection } from './ChapterTestSection'
import { CompletedReviewSection } from './CompletedReviewSection'
import { SessionCommentEditor, type SessionDraft } from './SessionCommentEditor'

interface Props {
  report: ChapterReportView
  editable?: boolean
  selectedLessonIds: number[]
  overallComment: string
  sessionDrafts?: Record<number, SessionDraft>
  savingAttendanceId?: number | null
  onRecommendationsChange?: (lessonIds: number[]) => void
  onOverallCommentChange?: (comment: string) => void
  onSessionDraftChange?: (attendanceId: number, note: string) => void
  onSessionSave?: (attendanceId: number) => void
}

export function ChapterReportDocument({
  report,
  editable = false,
  selectedLessonIds,
  overallComment,
  sessionDrafts,
  savingAttendanceId,
  onRecommendationsChange,
  onOverallCommentChange,
  onSessionDraftChange,
  onSessionSave,
}: Props) {
  const { t } = useTranslation('coaching')
  const showOverall = editable || overallComment.trim().length > 0

  return (
    <article
      className="chapter-report-document mx-auto w-full max-w-[794px] bg-white px-12 pb-12 pt-10 font-sans text-[#131313]"
      data-testid="chapter-report-document"
    >
      <ChapterReportHeader report={report} />
      <div className="mt-8 space-y-8">
        <ChapterLearningSection report={report} />
        <ChapterTestSection
          report={report}
          selectedLessonIds={selectedLessonIds}
          editable={editable}
          onRecommendationsChange={onRecommendationsChange}
        />
        <CompletedReviewSection report={report} />
        {showOverall && (
          <section className="chapter-report-row space-y-2" data-testid="chapter-report-overall-comment">
            <h2 className="px-2 text-base font-bold text-[#131313]">
              {t('chapterReport.overallComment')}
            </h2>
            {editable ? (
              <>
                <p className="px-2 text-xs leading-[1.5] text-[#ff675e]">
                  {t('chapterReport.overallEditHelp')}
                </p>
                <label htmlFor="chapter-report-overall-comment" className="sr-only">
                  {t('chapterReport.overallComment')}
                </label>
                <textarea
                  id="chapter-report-overall-comment"
                  rows={6}
                  value={overallComment}
                  onChange={(event) => onOverallCommentChange?.(event.target.value)}
                  placeholder={t('chapterReport.overallPlaceholder')}
                  className="w-full resize-y rounded-xl border border-[#d0d0d0] bg-[#edf5ff] px-5 py-4 text-[length:max(13px,var(--quebon-form-font-size-min,0px))] leading-[1.6] outline-none focus:border-[#4599ff] focus:ring-2 focus:ring-[#4599ff]/20"
                />
              </>
            ) : (
              <div className="space-y-2.5 rounded-xl bg-[#edf5ff] px-5 py-4 text-[13px] leading-[1.6]">
                {selectedLessonIds.length === 0 ? (
                  <p>
                    {t('chapterReport.supplementSummaryEmptyBefore')}
                    <strong className="text-[#4599ff]">
                      {t('chapterReport.supplementSummaryEmptyHighlight')}
                    </strong>
                    {t('chapterReport.supplementSummaryEmptyAfter')}
                  </p>
                ) : (
                  <p>
                    {t('chapterReport.supplementSummaryBefore')}
                    <strong className="text-[#4599ff]">
                      {t('chapterReport.supplementSummaryCount', {
                        count: selectedLessonIds.length,
                      })}
                    </strong>
                    {t('chapterReport.supplementSummaryAfter')}
                  </p>
                )}
                <p className="whitespace-pre-wrap">{overallComment}</p>
              </div>
            )}
          </section>
        )}
        <SessionCommentEditor
          learnerName={report.learner.fullName}
          sessions={report.classSessions}
          drafts={sessionDrafts}
          editable={editable}
          savingAttendanceId={savingAttendanceId}
          onDraftChange={onSessionDraftChange}
          onSave={onSessionSave}
        />
      </div>
    </article>
  )
}
